import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import { Box, Button, Grid, Paper, TextField, Typography, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';

const INITIAL_FILTERS = {
  search: '',
  fromDate: '',
  toDate: '',
  requestId: '',
  requestorName: '',
  accountName: '',
};

const formatTimestamp = (value) => (value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-');

const parseJsonField = (value) => {
  if (!value || typeof value !== 'string') {
    return value ?? {};
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const buildSolrPayload = ({
  search,
  fromDate,
  toDate,
  requestId,
  requestorName,
  accountName,
}) => {
  const trimmedSearch = search?.trim();

  let queryBody = trimmedSearch && trimmedSearch.length > 0 ? trimmedSearch : '*:*';

  const addClause = (clause) => {
    queryBody = `${queryBody} AND ${clause}`;
  };

  if (fromDate || toDate) {
    const start = fromDate ? new Date(fromDate).toISOString() : '*';

    const end = toDate ? new Date(toDate).toISOString() : '*';

    addClause(`created_date:[${start} TO ${end}]`);
  }

  // if (requestId?.trim()) {
  //   addClause(`request_id:${requestId.trim()}`);
  // }

  if (requestorName?.trim()) {
    addClause(`owner_name:${requestorName.trim()}`);
  }

  if (accountName?.trim()) {
    addClause(`account_name:${accountName.trim()}`);
  }

  addClause('mdm_dcr_status:Closed');

  return {
    q: `${queryBody}&rows=200`,
  };
};

const getRequestKey = (row, index) => row.request_id || row.requestId || row.id || `row-${index}`;

const getRequestId = (row) => row.request_id || row.requestId || row.id || '';

const getDisplayValue = (row, keys) =>
  keys
    .map((key) => row?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') || '-';

const UserHistory = () => {
  const navigate = useNavigate();

  const { loading, data, execute } = useAPICall();
  const [filters, setFilters] = useState({ ...INITIAL_FILTERS });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const initialFiltersRef = useRef(INITIAL_FILTERS);

  const fetchHistory = useCallback(
    async (filterValues) => {
      const payload = buildSolrPayload(filterValues);

      await execute(API_CONFIG.history.getRequestHistory, payload, {
        errorMessage: 'Unable to load user history.',
        retries: 1,
      });
    },
    [execute]
  );

  useEffect(() => {
    fetchHistory(initialFiltersRef.current).catch(() => {
      // handled by hook
    });
  }, [fetchHistory]);

  const rows = useMemo(() => {
    const docs = data?.response?.docs;

    if (!Array.isArray(docs)) {
      return [];
    }

    return docs.map((row, index) => ({
      ...row,
      owner_details: parseJsonField(row.owner_details),
      account_details: parseJsonField(row.account_details),
      requestKey: getRequestKey(row, index),
    }));
  }, [data]);

  const tableColumns = useMemo(
    () => [
      {
        header: 'MDM ID',
        id: 'mdm_id',
        accessorFn: (row) => row.mdm_id || '-',
      },
      {
        header: 'Name',
        id: 'account',
        accessorFn: (row) => {
          const name = row.account_details?.name || row.account_name;
          return name && name.trim() !== '' ? name : '-';
        },
      },
      {
        header: 'DCR ID',
        id: 'requestId',
        accessorFn: (row) => {
          if (row.source === 'VEEVA') {
            return row.request_id || getRequestId(row) || '-';
          }
          return getRequestId(row) || '-';
        },
      },
      // {
      //   header: 'Request Type',
      //   id: 'request_type',
      //   accessorFn: (row) => (row.request_type ? String(row.request_type).split('_')[0] : '-'),
      // },
      {
        header: 'Record Type',
        id: 'record_type',
        accessorFn: (row) => {
          const val = getDisplayValue(row, ['record_type']);
          return val !== '-' ? String(val).split('_')[0] : '-';
        },
      },
      {
        header: 'Source',
        id: 'source',
        accessorFn: (row) => row.source || '-',
      },
      {
        header: 'Requestor Name',
        id: 'requestor',
        accessorFn: (row) => {
          const name = row.owner_details?.name || row.owner_name;
          return name && name.trim() !== '' ? name : '-';
        },
      },
      {
        header: 'Status',
        id: 'status',
        accessorFn: (row) => row.mdm_dcr_status || row.status || row.requestStatus || '-',
      },
      {
        header: 'Updated At',
        id: 'updatedAt',
        accessorFn: (row) =>
          formatTimestamp(
            row.updated_at || row.modified_at || row.last_modified_date || row.created_at
          ),
      },
    ],
    []
  );

  const handleSearch = async () => {
    try {
      setPage(1);
      await fetchHistory(filters);
    } catch {
      // handled
    }
  };

  const handleClearFilters = async () => {
    setFilters({ ...INITIAL_FILTERS });
    setPage(1);

    try {
      await fetchHistory(INITIAL_FILTERS);
    } catch {
      // handled
    }
  };

  const totalRequests = data?.response?.numFound ?? rows.length;

  const softSearchSx = {
    '& .MuiOutlinedInput-root': {
      mt: '4px !important', // reduce spacing
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      height: '38px',
      transition: 'all 0.2s',
      '& fieldset': { borderColor: '#e2e8f0' },
      '&:hover fieldset': { borderColor: '#cbd5e1' },
      '&.Mui-focused fieldset': { borderColor: '#1E88E5', borderWidth: '1.5px' },
      '&.Mui-focused': { backgroundColor: '#fff' }
    },
    '& .MuiOutlinedInput-input': { py: 0, px: 1.5, height: '100%', boxSizing: 'border-box' }
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {/* FILTER SECTION */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              backgroundColor: '#ffffff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              // mb: 2,
            }}
          >
            <Box sx={{ borderBottom: '1px solid #eef2f7', p: 2.5,pb: 0.5}}>
            <Typography sx={{ fontWeight: 600,  color: '#111827', mb: 0.5 }}>
              DCR History
            </Typography>

            <Typography sx={{ fontWeight: 500, color: '#64748b',  mb: 0.5 ,}}>
              Browse closed requests, filter by search criteria, and inspect request details
            </Typography>  
            </Box>
            <Grid container spacing={2} sx={{ p: 2.5,}}>
            <Grid item xs={12} md={4}>
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: '#334155' }}
                  >
                    Search
                  </Typography>

                  <Tooltip
                    title="You can search by requestor details (name, email, employee no, primary territory), account name, request id, gender, suffix, state & zip"
                    placement="bottom"
                    arrow
                  >
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: 16,
                        cursor: 'pointer',
                        color: 'text.secondary',
                      }}
                    />
                  </Tooltip>
                </Box>

                <TextField
                  fullWidth
                  value={filters.search}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      search: event.target.value,
                    }))
                  }
                  placeholder="Search by name, account, territory..."
                  size="small"
                  sx={softSearchSx}
                />
              </Box>
            </Grid>

              <Grid item xs={12} md={4}>
                   <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: '#334155' }}
                  >
                   Created Start Date
                  </Typography>
                  </Box>
                <TextField
                  fullWidth
                  type="date"
                  value={filters.fromDate}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      fromDate: event.target.value,
                    }))
                  }
                  sx={softSearchSx}
                  size="small"
                />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                       <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: '#334155' }}
                  >
                 Created End Date
                  </Typography>
                  </Box>
                <TextField
                  fullWidth
                  type="date"
                  value={filters.toDate}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      toDate: event.target.value,
                    }))
                  }
                  sx={softSearchSx}
                  InputLabelProps={{
                    shrink: true,
                    sx: {  position: 'relative', mb: 0, fontWeight: 500, color: '#334155', '&.Mui-focused': { color: '#334155' } }
                  }}
                  size="small"
                />
                </Box>
              </Grid>

              {/* <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Request Id"
                  value={filters.requestId}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      requestId: event.target.value,
                    }))
                  }
                  placeholder="Request Id"
                  size="small"
                  sx={softSearchSx}
                  InputLabelProps={{ shrink: true, sx: {  position: 'relative', mb: 0, fontWeight: 500, color: '#334155', '&.Mui-focused': { color: '#334155' } } }}
                />
              </Grid> */}

              <Grid item xs={12} md={4}>
                       <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: '#334155' }}
                  >
                  Requestor Name
                  </Typography>
                  </Box>
                <TextField
                  fullWidth
                  value={filters.requestorName}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      requestorName: event.target.value,
                    }))
                  }
                  placeholder="Requestor Name"
                  size="small"
                  sx={softSearchSx}
                  InputLabelProps={{ shrink: true, sx: {  position: 'relative', mb: 0, fontWeight: 500, color: '#334155', '&.Mui-focused': { color: '#334155' } } }}
                />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                       <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: '#334155' }}
                  >
                  HCP Name
                  </Typography>
                  </Box>
                <TextField
                  fullWidth
                  value={filters.accountName}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      accountName: event.target.value,
                    }))
                  }
                  placeholder="HCP Name"
                  size="small"
                  sx={softSearchSx}
                  InputLabelProps={{ shrink: true, sx: {  position: 'relative', mb: 0, fontWeight: 500, color: '#334155', '&.Mui-focused': { color: '#334155' } } }}
                />
                </Box>
              </Grid>

              <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    gap: 1.5,
                    width: '100%',
                  }}
                >
                  <Button className="action-btn process-btn action-btn process-btn"
                    variant="contained"
                    onClick={handleSearch}
                    disabled={loading}
                    sx={{
                      minWidth: 120,
                      height: 38,
                      '&.Mui-disabled': {
                        backgroundColor: alpha('#1976d2', 0.12),
                        color: alpha('#1976d2', 0.3),
                      },
                    }}
                  >
                    Search
                  </Button>

                  <Button className="action-btn cancel-btn action-btn cancel-btn"
                    variant="outlined"
                    onClick={handleClearFilters}
                    disabled={loading}
                    sx={{
                      minWidth: 120,
                      height: 38,
                      '&.Mui-disabled': {
                        backgroundColor: alpha('#1976d2', 0.05),
                        color: alpha('#1976d2', 0.3),
                        borderColor: alpha('#1976d2', 0.1),
                      },
                    }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* TABLE SECTION */}
        <Grid item xs={12}>
          <Box>
            <GlobalMaterialTable
              columns={tableColumns}
              data={rows}
              isLoading={loading}
              emptyMessage="No records found"
              enablePagination
              enableBottomToolbar
              manualPagination={false}
              page={page}
              pageSize={pageSize}
              onPageChange={(p) => setPage(p)}
              onPageSizeChange={(s) => setPageSize(s)}
              tableTitle="Closed Requests"
              tableCount={totalRequests}

              muiSearchTextFieldProps={{
                size: 'small',
                placeholder: 'Search...',
              }}
              getRowId={(row) => row.requestKey}
              onRowClick={(row) => {
                const reqId = row.requestId || row.request_id || row.id || row.requestKey;
                navigate(`/history/request-details/${reqId}`, { state: { requestData: row } });
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserHistory;
